import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { callSvcAxiosInstance } from "@/constants";
import { calendarServiceEndpoints } from "@/constants/endpoints";
import { createQueryKey } from "@/constants/query-keys";
import { buildCalendarCapacityOptions } from "@/pages/performance/calendar-capacities/utils/calendar-capacities.utils";

export const calendarCapacitiesKeys = {
    snapshot: () => createQueryKey.calendarCapacities(),
};

/** GET /calendar/capacities — one entry per calendar for a given date. */
export type CalendarCapacity = {
    name: string;
    maxAppointments: number;
    bookedAppointments: number;
    appointmentsPerSlot: number;
};

/** Date (YYYY-MM-DD) → calendar name → capacity. */
export type CalendarCapacitiesResponse = Record<string, Record<string, CalendarCapacity>>;

export type DashboardCalendarCapacityRow = {
    name: string;
    label: string;
    color: string;
    calendarId?: string;
    agentCount: number;
    todayBooked: number;
    todayMax: number;
    tomorrowBooked: number;
    tomorrowMax: number;
};

export type CalendarCapacitiesDashboardData = {
    todayDate?: string;
    tomorrowDate?: string;
    rows: DashboardCalendarCapacityRow[];
};

const defaultColor = "hsl(var(--primary))";

/** Maps the API map to dashboard rows using the first two sorted dates as today / tomorrow. */
export function toCalendarCapacitiesDashboardData(
    capacities: CalendarCapacitiesResponse
): CalendarCapacitiesDashboardData {
    const [todayDate, tomorrowDate] = Object.keys(capacities).sort();
    const todayByName = todayDate ? (capacities[todayDate] ?? {}) : {};
    const tomorrowByName = tomorrowDate ? (capacities[tomorrowDate] ?? {}) : {};
    const names = [...new Set([...Object.keys(todayByName), ...Object.keys(tomorrowByName)])].sort();

    const rows = names.map((name) => {
        const today = todayByName[name];
        const tomorrow = tomorrowByName[name];
        return {
            name,
            label: today?.name ?? tomorrow?.name ?? name,
            color: defaultColor,
            agentCount: today?.appointmentsPerSlot ?? tomorrow?.appointmentsPerSlot ?? 0,
            todayBooked: today?.bookedAppointments ?? 0,
            todayMax: today?.maxAppointments ?? 0,
            tomorrowBooked: tomorrow?.bookedAppointments ?? 0,
            tomorrowMax: tomorrow?.maxAppointments ?? 0,
        };
    });

    return { todayDate, tomorrowDate, rows };
}

type UseCalendarCapacitiesSnapshotOptions = {
    enabled?: boolean;
};

export const useCalendarCapacitiesSnapshot = ({ enabled = true }: UseCalendarCapacitiesSnapshotOptions = {}) => {
    return useQuery({
        queryKey: calendarCapacitiesKeys.snapshot(),
        queryFn: async (): Promise<CalendarCapacitiesDashboardData> => {
            const response = await callSvcAxiosInstance.get<CalendarCapacitiesResponse>(
                calendarServiceEndpoints.getCalendarCapacities
            );
            return toCalendarCapacitiesDashboardData(response.data);
        },
        enabled,
        staleTime: 30_000,
    });
};

type UseDashboardCalendarCapacitiesOptions = {
    enabled?: boolean;
};

/** Snapshot capacities parsed for the dashboard widget and performance page. */
export function useDashboardCalendarCapacities({ enabled = true }: UseDashboardCalendarCapacitiesOptions = {}) {
    const { data, isLoading, isError, error, refetch } = useCalendarCapacitiesSnapshot({ enabled });
    const rows = data?.rows ?? [];

    const isEmptySnapshot = enabled && !isLoading && !isError && rows.length === 0;

    return {
        rows,
        todayDate: data?.todayDate,
        tomorrowDate: data?.tomorrowDate,
        isLoading: enabled && isLoading,
        isError: enabled && isError,
        error,
        refetch,
        isEmptySnapshot,
    };
}

type UseCalendarCapacitiesOptionsParams = {
    enabled?: boolean;
};

/** Allowed calendar options for capacity selectors and remote config. */
export function useCalendarCapacitiesOptions({ enabled = true }: UseCalendarCapacitiesOptionsParams = {}) {
    const { rows } = useDashboardCalendarCapacities({ enabled });

    const options = useMemo(() => buildCalendarCapacityOptions([], rows), [rows]);
    const allCalendarNames = useMemo(() => options.map((option) => option.name), [options]);

    return { options, allCalendarNames };
}
